import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { BaseTable } from '@/components';
import { useActiveTab, useAppFilters, useIsDMAdmin } from '@/hooks';
import { useFetchMaintenanceData } from '@/hooks/AssetsPreviewPage/Maintenance/useFetchMaintenanceData';
import { useMaintenanceTable } from '@/hooks/AssetsPreviewPage/Maintenance';
import { ASSET_STUDIO, ASSETS_PREVIEW } from '@/types/shared/navigation-tabs.enums';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { fequencyCounter } from '@/utils/helpers/fequencyCounter';

interface MaintenanceDataProps {
  components?: any;
  enableEditing?: boolean;
  setMaintenanceCounters?: any;
}

export const MaintenanceTable = ({
  components,
  enableEditing = true,
  setMaintenanceCounters,
}: MaintenanceDataProps) => {
  const { id } = useParams<string>();
  const { selectedAsset, selectedComponent, assetStudio, assetsPreview } = useAppFilters();
  const { isMobileAsset } = useActiveAssetsType();
  const { maintenanceData, isLoadingMaintenanceData } = useFetchMaintenanceData({ assetId: id || selectedAsset });
  const { isDMAdmin } = useIsDMAdmin();

  const tableData = useMemo(() => {
    const assetPreviewData =
      selectedComponent === ASSET_STUDIO.ALL_COMPONENTS
        ? maintenanceData
        : selectedComponent === ASSET_STUDIO.UNASSIGNED
        ? maintenanceData?.filter((el: any) => !el?.componentId)
        : maintenanceData?.filter((el: any) => el?.componentId === selectedComponent);
    return (
      (maintenanceData && (assetsPreview === ASSETS_PREVIEW.MAINTENANCE_DATA ? maintenanceData : assetPreviewData)) ||
      []
    );
  }, [maintenanceData, selectedComponent, assetsPreview]);

  const { tableStructure, renderEditRow } = useMaintenanceTable({
    DMComponents: components,
    data: tableData,
    hideEmptySecondaryWorkOrder: true, // Set to false to always show the column
  });

  useEffect(() => {
    setMaintenanceCounters &&
      setMaintenanceCounters({
        total: maintenanceData?.length || 0,
        unassigned: maintenanceData?.length - maintenanceData?.filter((el: any) => el.componentUiName).length || 0,
        components: fequencyCounter(maintenanceData, ['componentId']),
      });
  }, [maintenanceData, setMaintenanceCounters, assetStudio]);

  return (
    <div className="flex gap-2 h-[calc(100vh-11rem)] overflow-hidden">
      {isLoadingMaintenanceData ? (
        <Skeleton className="w-full min-h-full" />
      ) : tableData.length > 0 ? (
        <BaseTable
          enableSearch={true}
          enableEditing={isDMAdmin && enableEditing && isMobileAsset}
          renderEditRow={renderEditRow}
          data={tableData}
          columns={tableStructure.filter((el: any) => {
            if (el.accesorKey === 'DMComponents') return isDMAdmin;
            else return true;
          })}
          wrapHeaders={false}
          classNames={{
            card: {
              container: 'h-full w-full max-h-[calc(100vh-11rem)] max-w-[calc(100%)] overflow-x-auto',
              content: 'p-0 m-2 overflow-y-hidden max-h-[calc(100vh-11rem)] ',
            },
            table: {
              container:
                'overflow-auto h-full w-full sticky top-0  min-h-[calc(100vh-15.8rem)] max-h-[calc(100vh-15.8rem)]',
              th: '[&>*]:text-left [&>*]:text-subtext [&>*]:font-medium [&>*]:text-[14px] py-2',
              tr: 'h-14 bg-white dark:bg-[#52606d]',
              td: 'group-hover:bg-gray-100 dark:group-hover:bg-[#2f3844]',
            },
          }}
        />
      ) : (
        <div className="min-h-full min-w-full flex items-center justify-center font-semibold">No Maintenance Data</div>
      )}
    </div>
  );
};
